<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';

const TEMPLATE_LANGUAGES = [
  { value: 'es', label: 'Español' },
  { value: 'es_AR', label: 'Español (Argentina)' },
  { value: 'es_MX', label: 'Español (México)' },
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'pt_PT', label: 'Português (Portugal)' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'ru', label: 'Русский' },
];

const TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const STATUS_COLORS = {
  APPROVED: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  REJECTED: 'bg-red-100 text-red-800',
  PAUSED: 'bg-gray-100 text-gray-800',
};

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappReauthorize,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      agentSignatureEnabled: false,
      isUpdatingSignature: false,
      showCreateTemplate: false,
      isCreatingTemplate: false,
      isEditingTemplate: false,
      selectedTemplateId: null,
      isDeletingTemplate: false,
      templateLanguages: TEMPLATE_LANGUAGES,
      templateCategories: TEMPLATE_CATEGORIES,
      statusColors: STATUS_COLORS,
      metaMessagingTierLimit: 0,
      newTemplate: {
        name: '',
        category: 'MARKETING',
        language: 'es',
        headerType: 'NONE',
        headerText: '',
        headerHandle: '',
        bodyText: '',
        footerText: '',
        buttons: []
      },
      isUploadingMedia: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    headerFileAccept() {
      if (this.newTemplate.headerType === 'IMAGE') return 'image/jpeg,image/png';
      if (this.newTemplate.headerType === 'VIDEO') return 'video/mp4';
      if (this.newTemplate.headerType === 'DOCUMENT') return 'application/pdf,application/msword,text/plain';
      return '*/*';
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    inboxTemplates() {
      return this.inbox.message_templates || [];
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.agentSignatureEnabled =
        !!this.inbox.provider_config?.agent_signature_enabled;
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    async toggleAgentSignature() {
      this.isUpdatingSignature = true;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              agent_signature_enabled: this.agentSignatureEnabled,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingSignature = false;
      }
    },
    templateStatusClass(status) {
      return this.statusColors[status] || 'bg-gray-100 text-gray-600';
    },
    templateBodyText(template) {
      const body = (template.components || []).find(c => c.type === 'BODY');
      return body?.text || '';
    },
    resetNewTemplate() {
      this.newTemplate = {
        name: '',
        language: 'es',
        category: 'MARKETING',
        headerType: 'NONE',
        headerText: '',
        headerHandle: '',
        bodyText: '',
        footerText: '',
        buttons: [],
      };
      this.showCreateTemplate = false;
      this.isEditingTemplate = false;
      this.selectedTemplateId = null;
    },
    editTemplate(template) {
      const header = template.components?.find(c => c.type === 'HEADER');
      const body = template.components?.find(c => c.type === 'BODY');
      const footer = template.components?.find(c => c.type === 'FOOTER');
      const buttonsComponent = template.components?.find(c => c.type === 'BUTTONS');
      
      this.newTemplate = {
        name: template.name,
        category: template.category,
        language: template.language || 'es',
        headerType: header ? header.format : 'NONE',
        headerText: header?.text || '',
        headerHandle: '',
        bodyText: body?.text || '',
        footerText: footer?.text || '',
        buttons: (buttonsComponent?.buttons || []).map(b => ({ ...b }))
      };
      
      this.selectedTemplateId = template.id || template.name;
      this.isEditingTemplate = true;
      this.showCreateTemplate = true;
    },
    async deleteTemplate(template) {
      if (!window.confirm(`¿Estás seguro de que deseas eliminar la plantilla "${template.name}"? Esto eliminará todos sus idiomas asociados.`)) {
        return;
      }
      this.isDeletingTemplate = true;
      try {
        await this.$store.dispatch('inboxes/deleteTemplate', {
          inboxId: this.inbox.id,
          name: template.name,
        });
        useAlert('Plantilla eliminada exitosamente');
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      } finally {
        this.isDeletingTemplate = false;
      }
    },
    addButton(type) {
      if (type === 'QUICK_REPLY') {
        if (this.newTemplate.buttons.filter(b => b.type === 'QUICK_REPLY').length >= 3) return;
        this.newTemplate.buttons.push({ type: 'QUICK_REPLY', text: '' });
      } else if (type === 'URL') {
        if (this.newTemplate.buttons.find(b => b.type === 'URL')) return;
        this.newTemplate.buttons.push({ type: 'URL', text: '', url: '' });
      } else if (type === 'PHONE_NUMBER') {
        if (this.newTemplate.buttons.find(b => b.type === 'PHONE_NUMBER')) return;
        this.newTemplate.buttons.push({ type: 'PHONE_NUMBER', text: '', phone_number: '' });
      }
    },
    async handleHeaderFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isUploadingMedia = true;
      try {
        const response = await this.$store.dispatch('inboxes/uploadMediaForTemplate', {
          inboxId: this.inbox.id,
          file,
        });
        this.newTemplate.headerHandle = response.handle;
      } catch (error) {
        useAlert(error.message || 'Error al subir archivo');
        event.target.value = null;
      } finally {
        this.isUploadingMedia = false;
      }
    },
    removeButton(index) {
      this.newTemplate.buttons.splice(index, 1);
    },
    extractVariablesCount(text) {
      if (!text) return 0;
      const matches = text.match(/\{\{(\d+)\}\}/g);
      if (!matches) return 0;
      const numbers = matches.map(m => parseInt(m.replace(/[^0-9]/g, ''), 10));
      return Math.max(...numbers);
    },
    buildTemplatePayload() {
      const components = [];
      if (this.newTemplate.headerType !== 'NONE') {
        if (this.newTemplate.headerType === 'TEXT' && this.newTemplate.headerText.trim()) {
          const headerText = this.newTemplate.headerText.trim();
          const headerVars = this.extractVariablesCount(headerText);
          const headerComponent = {
            type: 'HEADER',
            format: 'TEXT',
            text: headerText,
          };
          if (headerVars > 0) {
            headerComponent.example = {
              header_text: Array(headerVars).fill('ejemplo')
            };
          }
          components.push(headerComponent);
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(this.newTemplate.headerType)) {
          const headerComponent = {
            type: 'HEADER',
            format: this.newTemplate.headerType,
          };
          if (this.newTemplate.headerHandle) {
            headerComponent.example = {
              header_handle: [this.newTemplate.headerHandle]
            };
          }
          components.push(headerComponent);
        }
      }
      
      const bodyText = this.newTemplate.bodyText.trim();
      const bodyVars = this.extractVariablesCount(bodyText);
      const bodyComponent = {
        type: 'BODY',
        text: bodyText,
      };
      if (bodyVars > 0) {
        bodyComponent.example = {
          body_text: [Array(bodyVars).fill('ejemplo')]
        };
      }
      components.push(bodyComponent);

      if (this.newTemplate.footerText.trim()) {
        components.push({
          type: 'FOOTER',
          text: this.newTemplate.footerText.trim(),
        });
      }
      if (this.newTemplate.buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: this.newTemplate.buttons.map(btn => {
            if (btn.type === 'QUICK_REPLY') return { type: 'QUICK_REPLY', text: btn.text };
            if (btn.type === 'URL') return { type: 'URL', text: btn.text, url: btn.url };
            if (btn.type === 'PHONE_NUMBER') return { type: 'PHONE_NUMBER', text: btn.text, phone_number: btn.phone_number };
            return btn;
          }),
        });
      }
      return {
        name: this.newTemplate.name.trim(),
        language: this.newTemplate.language,
        category: this.newTemplate.category,
        components,
      };
    },
    async submitCreateTemplate() {
      if (!this.newTemplate.name || !this.newTemplate.bodyText) {
        useAlert('Name and body text are required');
        return;
      }

      // Validar que las variables no estén al principio ni al final del cuerpo
      const bodyTextTrimmed = this.newTemplate.bodyText.trim();
      if (/^\{\{\d+\}\}/.test(bodyTextTrimmed) || /\{\{\d+\}\}$/.test(bodyTextTrimmed)) {
        useAlert('Error: Las variables {{x}} no pueden estar al principio ni al final del texto del cuerpo. Por favor añade un texto o punto (.) al final o al inicio.');
        return;
      }

      // Validar que las variables no estén al principio ni al final del encabezado
      if (this.newTemplate.headerType === 'TEXT' && this.newTemplate.headerText.trim()) {
        const headerTextTrimmed = this.newTemplate.headerText.trim();
        if (/^\{\{\d+\}\}/.test(headerTextTrimmed) || /\{\{\d+\}\}$/.test(headerTextTrimmed)) {
          useAlert('Error: Las variables {{x}} no pueden estar al principio ni al final del texto del encabezado. Por favor añade texto.');
          return;
        }
      }
      
      // Valida si es multimedia que tenga handle (en creación es obligatorio)
      if (!this.isEditingTemplate && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(this.newTemplate.headerType) && !this.newTemplate.headerHandle) {
        useAlert('Debes subir un archivo para este tipo de encabezado multimedia');
        return;
      }
      this.isCreatingTemplate = true;
      try {
        if (this.isEditingTemplate) {
          const payload = this.buildTemplatePayload();
          delete payload.name;
          delete payload.language;
          delete payload.category;

          await this.$store.dispatch('inboxes/updateTemplate', {
            inboxId: this.inbox.id,
            templateId: this.selectedTemplateId,
            template: payload,
          });
          useAlert('Plantilla actualizada exitosamente');
        } else {
          await this.$store.dispatch('inboxes/createTemplate', {
            inboxId: this.inbox.id,
            template: this.buildTemplatePayload(),
          });
          useAlert('Plantilla creada y enviada a Meta para revisión');
        }
        this.resetNewTemplate();
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      } finally {
        this.isCreatingTemplate = false;
      }
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAVoiceChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
      :help-text="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_call_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')"
      :help-text="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_status_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          v-if="whatsappAppId"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="`${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')} ${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')}`"
        >
          <div class="flex flex-col gap-1 items-start">
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <!-- Agent Signature Toggle -->
      <SettingsToggleSection
        v-model="agentSignatureEnabled"
        header="Firma del agente"
        description="Agrega el nombre del agente al inicio de cada mensaje saliente de WhatsApp con formato *Nombre:* seguido del mensaje"
        @update:model-value="toggleAgentSignature"
      />

      <!-- Templates Section -->
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')"
      >
        <div class="flex gap-2 flex-wrap">
          <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
          </NextButton>
          <NextButton variant="faded" color="slate" @click="showCreateTemplate = true">
            Nueva Plantilla
          </NextButton>
        </div>
      </SettingsFieldSection>

      <!-- Template List -->
      <div v-if="inboxTemplates.length > 0" class="mt-2 mb-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">
          Plantillas ({{ inboxTemplates.length }})
        </p>
        <div class="space-y-2 max-h-64 overflow-y-auto">
          <div
            v-for="template in inboxTemplates"
            :key="template.id || template.name"
            class="flex items-start justify-between p-3 rounded-lg border border-n-weak bg-n-background"
          >
            <div class="flex-1 min-w-0 mr-3">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-medium text-n-slate-12 truncate">{{ template.name }}</span>
                <span
                  :class="templateStatusClass(template.status)"
                  class="text-xs px-2 py-0.5 rounded-full font-medium shrink-0"
                >
                  {{ template.status }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11 truncate">{{ templateBodyText(template) }}</p>
              <p class="text-xs text-n-slate-10 mt-0.5">{{ template.language }} · {{ template.category }}</p>
            </div>
            <div class="flex gap-2">
              <button 
                v-if="['APPROVED', 'REJECTED', 'PAUSED'].includes(template.status)"
                type="button" 
                class="text-xs text-n-blue-11 hover:underline"
                @click="editTemplate(template)"
              >
                Editar
              </button>
              <button 
                type="button" 
                class="text-xs text-red-600 hover:underline"
                @click="deleteTemplate(template)"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-sm text-n-slate-11 mb-4">
        Sin plantillas. Sincroniza o crea una nueva.
      </div>

      <!-- Create Template Modal -->
      <div
        v-if="showCreateTemplate"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="resetNewTemplate"
      >
        <div class="bg-white dark:bg-n-solid-3 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">{{ isEditingTemplate ? 'Editar Plantilla WhatsApp' : 'Nueva Plantilla WhatsApp' }}</h3>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">Nombre *</label>
                <input
                  :value="newTemplate.name"
                  type="text"
                  placeholder="ej: bienvenida_cliente"
                  :disabled="isEditingTemplate"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8 disabled:opacity-50"
                  @input="newTemplate.name = $event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, c => c === ' ' ? '_' : '')"
                />
                <p class="text-xs text-n-slate-10 mt-1">Solo letras minúsculas, números y guiones bajos</p>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">Idioma *</label>
                  <select
                    v-model="newTemplate.language"
                    class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                  >
                    <option
                      v-for="lang in templateLanguages"
                      :key="lang.value"
                      :value="lang.value"
                    >{{ lang.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">Categoría *</label>
                  <select
                    v-model="newTemplate.category"
                    class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                  >
                    <option
                      v-for="cat in templateCategories"
                      :key="cat.value"
                      :value="cat.value"
                    >{{ cat.label }}</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">Tipo de Encabezado</label>
                <select
                  v-model="newTemplate.headerType"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8 mb-2"
                >
                  <option value="NONE">Ninguno</option>
                  <option value="TEXT">Texto</option>
                  <option value="IMAGE">Imagen</option>
                  <option value="VIDEO">Video</option>
                  <option value="DOCUMENT">Documento</option>
                </select>

                <div v-if="newTemplate.headerType === 'TEXT'">
                  <input
                    v-model="newTemplate.headerText"
                    type="text"
                    placeholder="Texto del encabezado, usa {{1}} para variables"
                    class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                  />
                </div>
                <div v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(newTemplate.headerType)">
                  <input
                    type="file"
                    :accept="headerFileAccept"
                    @change="handleHeaderFileUpload"
                    class="w-full text-sm text-n-slate-12 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-n-blue-2 file:text-n-blue-11 hover:file:bg-n-blue-3 cursor-pointer"
                  />
                  <p v-if="isUploadingMedia" class="text-xs text-n-blue-11 mt-1">Subiendo archivo a Meta...</p>
                  <p v-else-if="newTemplate.headerHandle" class="text-xs text-n-slate-11 mt-1">Archivo adjunto listo.</p>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">Cuerpo del mensaje *</label>
                <textarea
                  v-model="newTemplate.bodyText"
                  rows="4"
                  placeholder="Hola {{1}}, tu pedido {{2}} está listo. Usa {{n}} para variables."
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8 resize-none"
                />
                <p class="text-xs text-n-slate-10 mt-1">Usa &#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;... para variables dinámicas</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">Pie de página (opcional)</label>
                <input
                  v-model="newTemplate.footerText"
                  type="text"
                  placeholder="ej: Responde STOP para cancelar"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                />
              </div>

              <!-- Buttons Section -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-2">Botones (opcional)</label>

                <!-- Added buttons list -->
                <div v-if="newTemplate.buttons.length > 0" class="space-y-2 mb-3">
                  <div
                    v-for="(btn, idx) in newTemplate.buttons"
                    :key="idx"
                    class="p-3 border border-n-weak rounded-lg"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
                        {{ btn.type === 'QUICK_REPLY' ? 'Respuesta Rápida' : btn.type === 'URL' ? 'Enlace URL' : 'Número de Teléfono' }}
                      </span>
                      <button
                        type="button"
                        class="text-xs text-red-600 hover:text-red-800"
                        @click="removeButton(idx)"
                      >Eliminar</button>
                    </div>
                    <input
                      v-model="btn.text"
                      type="text"
                      placeholder="Texto del botón (máx 25 caracteres)"
                      maxlength="25"
                      class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                      :class="btn.type !== 'QUICK_REPLY' ? 'mb-2' : ''"
                    />
                    <input
                      v-if="btn.type === 'URL'"
                      v-model="btn.url"
                      type="text"
                      placeholder="https://ejemplo.com/pagina"
                      class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                    />
                    <input
                      v-if="btn.type === 'PHONE_NUMBER'"
                      v-model="btn.phone_number"
                      type="text"
                      placeholder="+573001234567"
                      class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
                    />
                  </div>
                </div>

                <!-- Add button controls -->
                <div class="flex flex-wrap gap-2">
                  <button
                    v-if="newTemplate.buttons.filter(b => b.type === 'QUICK_REPLY').length < 3"
                    type="button"
                    class="text-xs px-3 py-1.5 border border-n-weak rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                    @click="addButton('QUICK_REPLY')"
                  >+ Respuesta rápida</button>
                  <button
                    v-if="!newTemplate.buttons.find(b => b.type === 'URL')"
                    type="button"
                    class="text-xs px-3 py-1.5 border border-n-weak rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                    @click="addButton('URL')"
                  >+ Enlace URL</button>
                  <button
                    v-if="!newTemplate.buttons.find(b => b.type === 'PHONE_NUMBER')"
                    type="button"
                    class="text-xs px-3 py-1.5 border border-n-weak rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                    @click="addButton('PHONE_NUMBER')"
                  >+ Número de teléfono</button>
                </div>
                <p class="text-xs text-n-slate-10 mt-1">Máx 3 respuestas rápidas, o hasta 1 URL + 1 teléfono. No mezclar tipos.</p>
              </div>
            </div>

            <div class="flex justify-end gap-3 mt-6">
              <NextButton variant="faded" color="slate" @click="resetNewTemplate">
                Cancelar
              </NextButton>
              <NextButton
                :is-loading="isCreatingTemplate"
                :disabled="!newTemplate.name || !newTemplate.bodyText"
                @click="submitCreateTemplate"
              >
                {{ isEditingTemplate ? 'Actualizar' : 'Crear y Enviar a Meta' }}
              </NextButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
